import {chunk} from '@benchling/js-utils';
import React from 'react';

import {GetAnalysisPipelineStep, getDataFrameSpecFromGetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {JOIN_TYPE_TO_TITLE} from 'analysis-tool/data-frame-creation/combine-data-frames/CombineDataFramesViewContants';
import {RESTIdentifiedWarehouseParameter} from 'analysis-tool/data-frame-creation/DataFrameCreationTypes';
import {STEP_TYPE_TO_CREATION_METHOD_SUMMARY} from 'analysis-tool/ui/main-view/MainViewConstants';
import {ReadOnlyDataFrameColumnRelationshipMultiSelector} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {PersistedDataFrameMetadataModalProps} from 'data-frames/modals/PersistedDataFrameMetadataModal';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {JSONValue, MakeFieldsRequired} from 'util/Types';

function stepToAdditionalGridCells(
  step: GetAnalysisPipelineStep
): Array<{component: React.ReactNode; label: string}> {
  if (step.definition.stepType === PipelineStepType.INITIALIZE_DATASET && step.definition.stepVersion === 2) {
    const config = getPipelineStepConfig(step, PipelineStepType.INITIALIZE_DATASET, 2);
    return [
      {
        component: config.dashboardName,
        label: 'Dashboard name',
      },
      {
        component: config.blockName,
        label: 'Block name',
      },
    ];
  } else if (step.definition.stepType === PipelineStepType.NOTEBOOK_TABLE_DATASET) {
    const config = getPipelineStepConfig(step, PipelineStepType.NOTEBOOK_TABLE_DATASET, 0);
    return [
      {
        component: <EntryChip id={config.entryId} size={ChipSize.MINI} />,
        label: 'Entry',
      },
      {
        component: config.tableName,
        label: 'Table name',
      },
    ];
  } else if (step.definition.stepType === PipelineStepType.JOIN) {
    return [
      {
        component: JOIN_TYPE_TO_TITLE[getPipelineStepConfig(step, PipelineStepType.JOIN, 0).joinType],
        label: 'Join type',
      },
      {
        component: step.inputStepDatas.find((stepData) => stepData.name === 'Left')!.dataFrames[0].name,
        label: 'Left',
      },
      {
        component: step.inputStepDatas.find((stepData) => stepData.name === 'Right')!.dataFrames[0].name,
        label: 'Right',
      },
    ];
  }

  return [];
}

function ParameterCol({
  parameterValue,
  parameterConfig,
}: {
  parameterValue: JSONValue;
  parameterConfig: Omit<RESTIdentifiedWarehouseParameter['config'], 'type'>;
}): JSX.Element {
  return (
    <Col sm={6} dataTestAttrs={{component: 'ParameterCol', key: parameterConfig.systemName}}>
      <FormGroup key={parameterConfig.id}>
        <FieldLabel label={`{{${parameterConfig.systemName}}}`} />
        <p>{parameterValue}</p>
      </FormGroup>
    </Col>
  );
}

function AdditionalMetadataModalContent({step}: {step: GetAnalysisPipelineStep}): JSX.Element | null {
  if (step.definition.stepType === PipelineStepType.INITIALIZE_DATASET && step.definition.stepVersion === 2) {
    const config = getPipelineStepConfig(step, PipelineStepType.INITIALIZE_DATASET, 2);
    return (
      <>
        {[...chunk(config.parameters, 2)].map(([parameter1, parameter2], index) => (
          <Row key={index}>
            <ParameterCol parameterConfig={parameter1.config} parameterValue={parameter1.valueJson} />
            {parameter2 != null && (
              <ParameterCol parameterConfig={parameter2.config} parameterValue={parameter2.valueJson} />
            )}
          </Row>
        ))}
        <pre {...getTestProps({component: 'AdditionalMetadataModalContent', key: 'parameterizedSqlQuery'})}>
          {config.parameterizedSQLQuery}
        </pre>
      </>
    );
  } else if (step.definition.stepType === PipelineStepType.REGISTRY_SCHEMA_DATASET) {
    const config = getPipelineStepConfig(step, PipelineStepType.REGISTRY_SCHEMA_DATASET, 0);
    return (
      <pre {...getTestProps({component: 'AdditionalMetadataModalContent', key: 'queryDescription'})}>
        {config.queryDescription}
      </pre>
    );
  } else if (step.definition.stepType === PipelineStepType.JOIN) {
    const config = getPipelineStepConfig(step, PipelineStepType.JOIN, 0);

    // There is no column mapping for positional joins (aka 'append')
    if (config.joinType === 'POSITIONAL') {
      return null;
    }

    const leftDataFrame = step.inputStepDatas.find(({name}) => name === 'Left')!.dataFrames[0];
    const rightDataFrame = step.inputStepDatas.find(({name}) => name === 'Right')!.dataFrames[0];

    return (
      <ReadOnlyDataFrameColumnRelationshipMultiSelector
        leftDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(leftDataFrame)}
        rightDataFrame={getDataFrameSpecFromGetAnalysisDataFrame(rightDataFrame)}
        relationships={config.joinConditions.map(({leftDataSourceColumn, rightDataSourceColumn}) => ({
          leftColumn: leftDataSourceColumn,
          rightColumn: rightDataSourceColumn,
        }))}
      />
    );
  }
  return null;
}

/**
 * Get step-specific content for the data frame metadata model, which is not generic to all data frames.
 * @param step The source step the created the data frame
 */
export default function getAdditionalDataFrameMetadataModalContent(
  step: GetAnalysisPipelineStep
): MakeFieldsRequired<
  Pick<PersistedDataFrameMetadataModalProps, 'additionalGridCells' | 'additionalContent'>,
  'additionalGridCells' | 'additionalContent'
> {
  return {
    additionalGridCells: [
      {
        component: STEP_TYPE_TO_CREATION_METHOD_SUMMARY[step.definition.stepType],
        label: 'Created from',
      },
      ...stepToAdditionalGridCells(step),
    ],
    additionalContent: <AdditionalMetadataModalContent step={step} />,
  };
}
